import { useState, useEffect, useCallback } from "react";
import apiClient from "../utils/apiClient";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Users,
  TrendingUp,
  Clock,
  AlertTriangle,
  DollarSign,
  ArrowRight,
  RefreshCw,
  ChevronDown,
  CheckCircle,
  XCircle,
  Filter,
  Loader2,
  BarChart3,
} from "lucide-react";

const STATUS_COLORS = {
  trialing: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  active: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  past_due: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  canceled: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
};

const HEALTH_COLORS = {
  healthy: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  at_risk: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  critical: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
};

export default function AdminCustomerSuccess() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [filter, setFilter] = useState({ status: "", risk: "" });
  const [showFilters, setShowFilters] = useState(false);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, custRes, funnelRes] = await Promise.all([
        apiClient.get("/admin/agent/customers/dashboard"),
        apiClient.get("/admin/agent/customers/list", { params: filter }),
        apiClient.get("/admin/agent/customers/activation-funnel"),
      ]);
      setDashboard(dashRes.data);
      setCustomers(custRes.data.customers);
      setFunnel(funnelRes.data);
    } catch (err) {
      console.error("Failed to load customer data", err);
      toast.error("Failed to load customer data");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const applyFilter = (key, value) => {
    setFilter(prev => ({ ...prev, [key]: value }));
  };

  if (loading && !dashboard) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-customer-success">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Customer Success</h1>
            <p className="text-sm text-muted-foreground">
              Track customer health, activation, and conversion
            </p>
          </div>
          <Button variant="outline" onClick={loadDashboard} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <MetricCard
            label="Total Customers"
            value={dashboard?.overview?.total_customers || 0}
            icon={Users}
            color="blue"
          />
          <MetricCard
            label="Trial Users"
            value={dashboard?.overview?.trial_users || 0}
            icon={Clock}
            color="amber"
          />
          <MetricCard
            label="Active Paid"
            value={dashboard?.overview?.active_paid || 0}
            icon={CheckCircle}
            color="green"
          />
          <MetricCard
            label="Past Due"
            value={dashboard?.overview?.past_due || 0}
            icon={AlertTriangle}
            color="amber"
          />
          <MetricCard
            label="Canceled"
            value={dashboard?.overview?.canceled || 0}
            icon={XCircle}
            color="red"
          />
          <MetricCard
            label="Est. MRR"
            value={`$${dashboard?.metrics?.estimated_mrr || 0}`}
            icon={DollarSign}
            color="green"
          />
        </div>

        {/* Plan Distribution */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-card rounded-lg border p-6">
            <h3 className="font-medium mb-4">Plan Distribution</h3>
            <div className="space-y-3">
              <PlanBar
                label="Starter"
                value={dashboard?.plan_distribution?.starter || 0}
                total={dashboard?.overview?.total_customers || 1}
                color="bg-blue-500"
              />
              <PlanBar
                label="Professional"
                value={dashboard?.plan_distribution?.professional || 0}
                total={dashboard?.overview?.total_customers || 1}
                color="bg-purple-500"
              />
              <PlanBar
                label="Enterprise"
                value={dashboard?.plan_distribution?.enterprise || 0}
                total={dashboard?.overview?.total_customers || 1}
                color="bg-indigo-500"
              />
            </div>
          </div>

          <div className="bg-card rounded-lg border p-6">
            <h3 className="font-medium mb-4">Key Metrics</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Recent Signups (7d)</span>
                <span className="font-medium">{dashboard?.metrics?.recent_signups_7d || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Conversions (30d)</span>
                <span className="font-medium">{dashboard?.metrics?.conversions_30d || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Trial Conversion Rate</span>
                <span className="font-medium">{funnel?.key_metrics?.trial_conversion_rate || 0}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Churn Rate</span>
                <span className={`font-medium ${(dashboard?.metrics?.churn_rate || 0) > 10 ? "text-red-500" : ""}`}>
                  {dashboard?.metrics?.churn_rate || 0}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Activation Funnel */}
        {funnel && (
          <div className="bg-card rounded-lg border p-6">
            <h3 className="font-medium mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Activation Funnel ({funnel.period_days} days)
            </h3>
            <div className="space-y-2">
              {funnel.funnel?.map((stage, idx) => (
                <FunnelStage
                  key={stage.stage}
                  stage={stage.stage}
                  count={stage.count}
                  percent={stage.percent}
                  dropOff={stage.drop_off}
                  isFirst={idx === 0}
                />
              ))}
            </div>
          </div>
        )}

        {/* Customer List */}
        <div className="bg-card rounded-lg border">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-medium">Customer List</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showFilters ? "rotate-180" : ""}`} />
            </Button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="p-4 border-b bg-secondary/30 flex flex-wrap gap-4">
              <select
                value={filter.status}
                onChange={(e) => applyFilter("status", e.target.value)}
                className="px-3 py-2 rounded-lg border bg-background text-sm"
              >
                <option value="">All Statuses</option>
                <option value="trialing">Trialing</option>
                <option value="active">Active</option>
                <option value="past_due">Past Due</option>
                <option value="canceled">Canceled</option>
              </select>
              <select
                value={filter.risk}
                onChange={(e) => applyFilter("risk", e.target.value)}
                className="px-3 py-2 rounded-lg border bg-background text-sm"
              >
                <option value="">All Risk Levels</option>
                <option value="churn">Churn Risk</option>
                <option value="inactive">Inactive</option>
                <option value="expiring">Trial Expiring</option>
              </select>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="customer-table">
              <thead className="bg-secondary/50">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">Workspace</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">Owner</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">Plan</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">Health</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">Activation</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {customers.map((customer) => (
                  <tr key={customer.workspace_id} className="hover:bg-secondary/30">
                    <td className="py-3 px-4">
                      <div className="font-medium text-sm">{customer.workspace_name}</div>
                      <div className="text-xs text-muted-foreground">{customer.workspace_id.slice(0, 8)}...</div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm">{customer.owner_name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{customer.owner_email}</div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm capitalize">{customer.plan_tier}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLORS[customer.status] || ""}`}>
                        {customer.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-1 rounded-full ${HEALTH_COLORS[customer.health_score] || ""}`}>
                        {customer.health_score}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <ActivationDot active={customer.activation?.first_document} title="Document" />
                        <ActivationDot active={customer.activation?.first_question} title="Question" />
                        <ActivationDot active={customer.activation?.first_citation} title="Citation" />
                        <ActivationDot active={customer.activation?.whatsapp} title="WhatsApp" />
                        <ActivationDot active={customer.activation?.telegram} title="Telegram" />
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
                {customers.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-muted-foreground">
                      No customers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

// Helper Components
function MetricCard({ label, value, icon: Icon, color }) {
  const colors = {
    blue: "bg-blue-50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400",
    green: "bg-green-50 dark:bg-green-950/20 text-green-600 dark:text-green-400",
    amber: "bg-amber-50 dark:bg-amber-950/20 text-amber-600 dark:text-amber-400",
    red: "bg-red-50 dark:bg-red-950/20 text-red-600 dark:text-red-400",
  };

  return (
    <div className="bg-card rounded-lg border p-4">
      <div className={`w-8 h-8 rounded-lg ${colors[color]} flex items-center justify-center mb-3`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function PlanBar({ label, value, total, color }) {
  const percent = Math.round((value / total) * 100);
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span>{label}</span>
        <span className="text-muted-foreground">{value} ({percent}%)</span>
      </div>
      <div className="h-2 bg-secondary rounded-full overflow-hidden">
        <div
          className={`h-full ${color} rounded-full transition-all`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

function FunnelStage({ stage, count, percent, dropOff, isFirst }) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-40 text-sm truncate">{stage}</div>
      <div className="flex-1">
        <div className="h-6 bg-secondary rounded-full overflow-hidden relative">
          <div
            className="h-full bg-primary/70 rounded-full transition-all"
            style={{ width: `${percent}%` }}
          />
          <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
            {count} ({percent}%)
          </span>
        </div>
      </div>
      {!isFirst && dropOff > 0 && (
        <div className="w-20 text-right text-xs text-red-500">
          -{dropOff}% drop
        </div>
      )}
    </div>
  );
}

function ActivationDot({ active, title }) {
  return (
    <div
      className={`w-2 h-2 rounded-full ${active ? "bg-green-500" : "bg-gray-300"}`}
      title={title}
    />
  );
}
